/**
 * Stories resource: TanStack-backed read + mutation hooks. Canonical client
 * interface for the `stories.*` procedures.
 *
 * `useStory` reads one story by key and `useStoryByUrl` by source-article url;
 * `useAllStories` reads the editorial dash's story list; `useCreateStory`,
 * `useDeleteStory`, and `useSetStorySpaces` mutate that list and
 * auto-invalidate it on success so the dashboard refetches.
 */
import { urlToKey } from '@repo/shared/keyutil';
import type { Story } from '@repo/shared/orpc/procedures/stories';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from 'component/translation';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { useSiloKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { storyQueryOptions } from './story-features';
import { usePaginatedQuery } from './use-paginated-query';
import { useSiloMutation } from './use-silo-mutation';

export type { Story } from '@repo/shared/orpc/procedures/stories';

/**
 * Page size `useAllStories` requests to pull every story in one page. Stop-gap
 * until real pagination — matches the `useAllAdmins`/`useAllBlocklist` pattern.
 */
const ALL_STORIES_LIMIT = 100000;

/**
 * One story, or `undefined` while loading, when disabled (`storyKey` is
 * `null`), or when the read fails. `stories.get` constructs the story from the
 * source page's OpenGraph data on first access, so a failure means either that
 * page could not be read (`OPENGRAPH_FETCH_FAILED`) or the url is not a story
 * url this silo accepts (`INVALID_STORY_URL`). Both read as `undefined`, so a
 * caller that shows story metadata shows nothing at all instead of a partial
 * card.
 *
 */
export function useStory(storyKey: string | null): Story | undefined {
    const siloKey = useSiloKey();
    const locale = useLanguage();
    const utils = useApiQueryUtils();
    const query = useQuery({
        ...storyQueryOptions(utils, { siloKey, storyKey: storyKey ?? '', locale }),
        enabled: storyKey !== null,
    });
    return query.data;
}

/**
 * The story for a source-article url, for a caller that holds the url rather
 * than a key — an `entryUrl` recorded on a comment, say. Derives the key the
 * way `stories.create` does on the server, so a url nobody has read before
 * still resolves. Returns `null` with no url, and otherwise behaves like
 * `useStory`.
 */
export function useStoryByUrl(url?: string): Story | null {
    return useStory(url ? urlToKey(url) : null) ?? null;
}

/**
 * Every story in the silo, flattened. The stories dashboard reads through
 * this.
 */
export function useAllStories(): { stories: Story[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const result = usePaginatedQuery({ procedure: utils.stories.list, input: { limit: ALL_STORIES_LIMIT } });
    const stories = useMemo(() => result.data?.pages.flatMap((page) => page.items) ?? [], [result.data]);
    return { stories, isLoading: result.isLoading };
}

function useInvalidateStories() {
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    const siloKey = useSiloKey();
    return () => fireAndForget(queryClient.invalidateQueries({ queryKey: utils.stories.list.key({ input: { siloKey } }) }));
}

/**
 * Creates a story from a URL. Auto-invalidates the silo's stories list on
 * success. Exposes the mutation's `mutateAsync` as `createStoryAsync` so call
 * sites don't have to rename it themselves.
 */
export function useCreateStory() {
    const utils = useApiQueryUtils();
    const invalidate = useInvalidateStories();
    const mutation = useSiloMutation({ procedure: utils.stories.create, onSuccess: invalidate });
    return { ...mutation, createStoryAsync: mutation.mutateAsync };
}

/**
 * Deletes a story. Auto-invalidates the silo's stories list on success.
 * Exposes the mutation's `mutateAsync` as `deleteStoryAsync` so call sites
 * don't have to rename it themselves.
 */
export function useDeleteStory() {
    const utils = useApiQueryUtils();
    const invalidate = useInvalidateStories();
    const mutation = useSiloMutation({ procedure: utils.stories.delete, onSuccess: invalidate });
    return { ...mutation, deleteStoryAsync: mutation.mutateAsync };
}

/**
 * Replaces a story's attached spaces. Auto-invalidates the silo's stories
 * list on success. Exposes the mutation's `mutateAsync` as
 * `setStorySpacesAsync` so call sites don't have to rename it themselves.
 */
export function useSetStorySpaces() {
    const utils = useApiQueryUtils();
    const invalidate = useInvalidateStories();
    const mutation = useSiloMutation({ procedure: utils.stories.setSpaces, onSuccess: invalidate });
    return { ...mutation, setStorySpacesAsync: mutation.mutateAsync };
}
